import gql from 'graphql-tag';
import { defaultApolloClient } from '@/vue-apollo';
import { FieldComponentParams } from '@/types/FieldComponent';
import { GraphQLField, GraphQLObjectType, DocumentNode } from 'graphql';
import { ApolloQueryResponse } from '@/utils/entity';
import ObjectField from '@/components/FormBuilder/fields/ObjectField.vue';
import ObjectFieldNormalizer from '@/classes/normalizers/field-normalizers/ObjectFieldNormalizer';
import TypeInspector from '@/classes/graphql-introspection/TypeInspector';
import FieldComponentFactory from './FieldComponentFactory';
import { FieldsExtractor } from '../graphql-introspection/types';

export interface ObjectFieldParams {
  name: string;
  value?: number;
}

export default class ObjectFieldFactory extends FieldComponentFactory {
  readonly component = ObjectField;

  constructor(
    private queries: GraphQLField<any, any, any>[],
    private fieldsExtractor: FieldsExtractor,
  ) {
    super();
  }

  support(field: GraphQLField<any, any, any>) {
    return this.supportType(field.type);
  }

  supportType(type: any) {
    return this.typeInspector.isObjectField(type) || this.typeInspector.isMutationObjectField(type);
  }


  async createComponent(params: FieldComponentParams) {
    const { field } = params;

    const items = await this.getOptions(field);

    const componentsParams = {
      ...params,
      props: {
        items,
        'item-text': 'label',
        'item-value': 'id',
        normalizer: new ObjectFieldNormalizer(),
        ...params.props,
      },
    };

    return super.createComponent(componentsParams);
  }

  private getOptions(field: GraphQLField<any, any, any>) {
    const { type } = field;
    if (!TypeInspector.isUnionType(type)) return [];
    const unionedType = type.getTypes()[0];
    const listingField = this.getAnotherFieldForListing(unionedType);
    const [, objectName] = unionedType.name.split('_');

    const queryName = `get${objectName}Listing`;
    const queryListingType = this.getQueryListingTypeBy(queryName);

    if (!queryListingType || !listingField) return [];

    const query = gql`{
      ${queryName} {
        edges {
          node {
            id
            ${listingField.name}
          }
        }
      }
    }`;

    return this.fetchOptions(query, listingField.name);
  }

  private getQueryListingTypeBy(queryName: string) {
    return this.queries.find((q) => q.name.includes(queryName));
  }

  private getAnotherFieldForListing(type: GraphQLObjectType) {
    const scalarFields = this.getScalarFieldsForListing(type);
    let field = scalarFields.find((f) => f.name.includes('legal_name'));
    if (!field) {
      field = scalarFields.find((f) => f.name.includes('name')) || scalarFields.find((f) => !f.name.includes('id'));
    }

    return field;
  }

  private getScalarFieldsForListing(type: GraphQLObjectType) {
    const fields = this.fieldsExtractor.extractFrom<GraphQLField<any, any, any>>(type);

    return fields.filter((f) => TypeInspector.isScalarType(f.type));
  }

  private async fetchOptions(query: DocumentNode, fieldName: string) {
    const result: any = await defaultApolloClient.query({
      query,
    });

    const firstValue: any = Object.values(result.data)[0];

    const items = firstValue.edges.map((item: ApolloQueryResponse) => {
      const { id } = item.node;
      const label = item.node?.[fieldName];

      return {
        id,
        label,
      };
    });

    return items;
  }
}
